import type {
  AgentId,
  AgentToolId,
  CliUpdateList,
  CliUpdateSnapshot,
  CommandSnapshot,
  DeploySnapshot,
  DirListing,
  FileContent,
  GitBranchesSnapshot,
  GitCommitDiff,
  GitDiff,
  GitDiffstat,
  GitSnapshot,
  HostPref,
  ModelCatalog,
  Project,
  ProviderModel,
  Run,
  Session,
  StoredEvent,
  UsageResponse,
} from './protocol.ts';
import type { ProjectRow } from './project.ts';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** Fires when any call comes back 401 so the shell can drop to the lock screen. */
export const onUnauthorized = new Set<() => void>();

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });
  if (res.status === 401) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (path !== '/api/auth/login') {
      for (const fn of onUnauthorized) fn();
    }
    throw new ApiError(401, body.error ?? 'unauthorized');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });

export const api = {
  login: (password: string) => call<{ ok: true }>('/api/auth/login', json({ password })),
  logout: () => call<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => call<{ ok: true; dictation: boolean }>('/api/me'),
  usage: () => call<UsageResponse>('/api/usage'),

  modelCatalog: (refresh = false) =>
    call<ModelCatalog>(`/api/models${refresh ? '?refresh=1' : ''}`, { cache: 'no-store' }),
  setEnabledModels: (models: ProviderModel[]) =>
    call<{ enabled: string[] }>('/api/models/enabled', {
      method: 'PUT',
      body: JSON.stringify({ models }),
    }),

  cliUpdates: (refresh = false, host = 'server') =>
    call<CliUpdateList>(`/api/cli-updates?host=${encodeURIComponent(host)}${refresh ? '&refresh=1' : ''}`, { cache: 'no-store' }),
  startCliUpdate: (agent: AgentId, host = 'server') =>
    call<CliUpdateSnapshot>(`/api/cli-updates/${agent}?host=${encodeURIComponent(host)}`, { method: 'POST' }),
  cancelCliUpdate: (agent: AgentId, host = 'server') =>
    call<{ cancelled: boolean }>(`/api/cli-updates/${agent}/cancel?host=${encodeURIComponent(host)}`, { method: 'POST' }),

  projects: () => call<ProjectRow[]>('/api/projects'),
  setProjectTools: (id: string, tools: AgentToolId[]) =>
    call<ProjectRow>(`/api/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ tools }),
    }),
  setPreferredHost: (id: string, preferredHost: HostPref) =>
    call<ProjectRow>(`/api/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ preferredHost }),
    }),
  git: (projectId: string) => call<GitSnapshot>(`/api/projects/${projectId}/git`),
  gitDiffstat: (projectId: string) =>
    call<GitDiffstat>(`/api/projects/${projectId}/git/diffstat`, { cache: 'no-store' }),
  gitDiff: (projectId: string, path: string) =>
    call<GitDiff>(`/api/projects/${projectId}/git/diff?path=${encodeURIComponent(path)}`),
  gitCommitDiff: (projectId: string, commit: string) =>
    call<GitCommitDiff>(`/api/projects/${projectId}/git/commits/${encodeURIComponent(commit)}`),
  commit: (projectId: string, message: string) =>
    call<GitSnapshot>(`/api/projects/${projectId}/git/commit`, json({ message })),
  push: (projectId: string) =>
    call<GitSnapshot>(`/api/projects/${projectId}/git/push`, { method: 'POST' }),
  gitBranches: (projectId: string) =>
    call<GitBranchesSnapshot>(`/api/projects/${projectId}/git/branches`),
  checkoutBranch: (projectId: string, branch: string) =>
    call<GitSnapshot>(`/api/projects/${projectId}/git/branches/checkout`, json({ branch })),
  reset: (projectId: string, commit: string, mode: 'soft' | 'hard') =>
    call<GitSnapshot>(`/api/projects/${projectId}/git/reset`, json({ commit, mode })),
  discardFile: (projectId: string, path: string) =>
    call<GitSnapshot>(`/api/projects/${projectId}/git/discard`, json({ path })),
  createBranch: (projectId: string, branch?: string) =>
    call<GitSnapshot>(
      `/api/projects/${projectId}/git/branches/create`,
      json(branch ? { branch } : {}),
    ),
  deployState: (projectId: string) =>
    call<{ configured: boolean; command: string | null; snapshot: DeploySnapshot | null }>(
      `/api/projects/${projectId}/deploy`,
    ),
  startDeploy: (projectId: string) =>
    call<DeploySnapshot>(`/api/projects/${projectId}/deploy`, { method: 'POST' }),
  reloadBlender: (projectId: string) =>
    call<{ ok: true; sha256: string }>(`/api/projects/${projectId}/blender/reload`, { method: 'POST' }),
  commandState: (projectId: string, commandId: string) =>
    call<{
      snapshot: CommandSnapshot | null;
      command: { id: string; label: string; defaultPort?: number };
    }>(`/api/projects/${projectId}/commands/${encodeURIComponent(commandId)}`),
  runCommand: (projectId: string, commandId: string, port: number) =>
    call<CommandSnapshot>(
      `/api/projects/${projectId}/commands/${encodeURIComponent(commandId)}`,
      json({ port }),
    ),
  stopCommand: (projectId: string, commandId: string, port: number) =>
    call<CommandSnapshot>(
      `/api/projects/${projectId}/commands/${encodeURIComponent(commandId)}/stop`,
      json({ port }),
    ),
  cancelDeploy: (projectId: string) =>
    call<{ cancelled: boolean }>(`/api/projects/${projectId}/deploy/cancel`, { method: 'POST' }),
  files: (projectId: string, path = '') =>
    call<DirListing>(`/api/projects/${projectId}/files?path=${encodeURIComponent(path)}`),
  fileContent: (projectId: string, path: string) =>
    call<FileContent>(`/api/projects/${projectId}/files/content?path=${encodeURIComponent(path)}`),
  setFileContent: (projectId: string, path: string, text: string) =>
    call<FileContent>(`/api/projects/${projectId}/files/content?path=${encodeURIComponent(path)}`, {
      method: 'PUT',
      body: JSON.stringify({ text }),
    }),
  vision: (projectId: string) => call<{ text: string }>(`/api/projects/${projectId}/vision`),
  setVision: (projectId: string, text: string) =>
    call<{ text: string }>(`/api/projects/${projectId}/vision`, {
      method: 'PUT',
      body: JSON.stringify({ text }),
    }),

  sessions: () => call<Session[]>('/api/sessions'),
  reorderSessions: (projectId: string, ids: string[]) =>
    call<Session[]>(`/api/projects/${projectId}/sessions/order`, {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    }),
  createSession: (projectId: string, title?: string) =>
    call<Session>('/api/sessions', json({ projectId, ...(title ? { title } : {}) })),
  session: (id: string) => call<{ session: Session; project: Project | null }>(`/api/sessions/${id}`),
  renameSession: (id: string, title: string) =>
    call<Session>(`/api/sessions/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
  setTask: (
    id: string,
    task: string,
    images?: { mime: string; data: string; id?: string }[],
    surface?: string[],
    title?: string,
  ) =>
    call<Session>(
      `/api/sessions/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          task,
          ...(title === undefined ? {} : { title }),
          ...(images === undefined ? {} : { images }),
          // Omitted, not empty: leaving it out keeps whatever surface is
          // stored, which is what adding a photo should do.
          ...(surface === undefined ? {} : { surface }),
        }),
      },
    ),
  updateSessionPrefs: (id: string, model: string, effort: string | null) =>
    call<Session>(
      `/api/sessions/${id}`,
      { method: 'PATCH', body: JSON.stringify({ model, effort }) },
    ),
  setSessionBacklogged: (id: string, backlogged: boolean) =>
    call<Session>(`/api/sessions/${id}`, { method: 'PATCH', body: JSON.stringify({ backlogged }) }),
  setSkipVerification: (id: string, skipVerification: boolean) =>
    call<Session>(
      `/api/sessions/${id}`,
      { method: 'PATCH', body: JSON.stringify({ skipVerification }) },
    ),
  setParallelTask: (id: string, parallelTask: boolean) =>
    call<Session>(
      `/api/sessions/${id}`,
      { method: 'PATCH', body: JSON.stringify({ parallelTask }) },
    ),
  setLogging: (id: string, logging: boolean) =>
    call<Session>(`/api/sessions/${id}`, { method: 'PATCH', body: JSON.stringify({ logging }) }),
  runTask: (id: string, model?: string, effort?: string | null) =>
    call<Session>(
      `/api/sessions/${id}/task/run`,
      json(model ? { model, effort: effort ?? null } : {}),
    ),
  createTask: (projectId: string, task: string) =>
    call<Session>('/api/tasks', json({ projectId, task })),
  runAllTasks: () =>
    call<{ queued: number; skipped: { sessionId: string; title: string; reason: string }[] }>(
      '/api/tasks/run-all',
      { method: 'POST' },
    ),
  deleteSession: (id: string) => call<void>(`/api/sessions/${id}`, { method: 'DELETE' }),

  events: (id: string, since = 0) => call<StoredEvent[]>(`/api/sessions/${id}/events?since=${since}`),
  send: (
    id: string,
    text: string,
    model: string,
    effort: string | null,
    images?: { mime: string; data: string }[],
  ) =>
    call<Run>(
      `/api/sessions/${id}/messages`,
      json({ text, model, effort, ...(images && images.length > 0 ? { images } : {}) }),
    ),
  imageUrl: (sessionId: string, imageId: string) => `/api/sessions/${sessionId}/images/${imageId}`,
  videoUrl: (sessionId: string, videoId: string) => `/api/sessions/${sessionId}/videos/${videoId}`,
  cancel: (id: string) =>
    call<{ cancelled: boolean; session: Session }>(`/api/sessions/${id}/cancel`, { method: 'POST' }),
};
